import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { getQueryFn } from "@/lib/queryClient";
import { assignedTaskCardClass, isAssignedToAssistant } from "@/components/tasks/assigned-task-highlight";
import { Calendar, Tag, CheckSquare } from "lucide-react";
import { format } from "date-fns";
import { sanitizeCategory as sanitizeTaskCategory } from "@/lib/categoryUtils";
import { apiRequest, queryClient, toastTaskMutationError } from "@/lib/queryClient";
import { closestCenter } from "@dnd-kit/core";
import { isPast, endOfDay } from "date-fns";
import type { Task } from "@/components/tasks/task-types";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Filter, Square, Target, Milestone, BookOpen, ListTodo, Plus,
         GripVertical, Users, Share2, ChevronDown, ChevronRight, UserPlus, X } from "lucide-react";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { DndContext } from "@dnd-kit/core";
import { SortableTaskItem, SharedTaskCard, GroupedTasksList } from "@/components/tasks/task-list-components";
import { StarredSubTasksSection } from "@/components/tasks/subtask-components";
import { TaskFilterBar } from "@/components/tasks/task-filter-bar";
import KanbanBoard from "@/components/kanban-board";

export function TaskListTabs({
  activeMainTab, setActiveMainTab,
  taskStats, filters, updateFilters,
  filteredAndSortedTasks, groupedFilteredTasks,
  taskSubtasks: subTasksByTaskId,
  starredSubTaskItems,
  areAllTasksSelected, toggleSelectAll,
  setIsCreateModalOpen,
  isOverdue, getLinkedItemName, getSubTaskProgress,
  toggleTaskMutation, toggleTopThreeMutation,
  setViewingTask, setCreateNoteForTask, setEditingTask, setDeletingTaskId,
  selectedTasks, setSelectedTasks, toggleTaskSelection,
  onShareTask,
  quickProgressPopoverId, setQuickProgressPopoverId,
  quickProgressPopoverNote, setQuickProgressPopoverNote,
  quickProgressMutation,
  onSubtaskToggle, onSubtaskStar, onSubtaskDateChange,
  onSubtaskTextChange, onSubtaskReorder, onSubtaskDelete,
  getHideCompleted, onToggleHideCompleted,
  onScheduleTask,
  collapsedGroups, toggleGroupCollapsed, handleDragEnd, sensors,
  assignedToMeStats, assignedToMeLoading, assignedToMeTasks, updateSharedTaskMutation,
  assignedToMeError, refetchAssignedToMe, tasksError, refetchTasks,
  sharedTasksLoading, sharedTasks, assignedToOthersLoading, assignedToOthersTasks,
  isFiltersExpanded, setIsFiltersExpanded,
  setIsQuickProgressOpen, setIsProgressLogOpen,
  categories, teamMembers,
  isBulkActionsOpen, setIsBulkActionsOpen,
  setPerTaskCompletedOverrides, setEditingColumn, setIsColumnDialogOpen,
  handleTaskMove, handleColumnDelete, handleColumnReorder, setCreateTaskColumnId,
  columns, columnsLoading, toast,
  tasks,
}: any) {
  const { user, isAuthenticated } = useAuth();
  const { data: assistantProfile } = useQuery<{ id: string } | null>({
    queryKey: ["/api/assistants/profile"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated && user?.requiresTwoFactor === true,
    retry: false,
  });
  const isAssistantViewer = !!assistantProfile;
  // Derive assigned-to-others filter state from filters prop
  const assignedToOthersFilter = (filters as any).assignedToOthersFilter ?? "all";
  const assignedToOthersSortBy = (filters as any).assignedToOthersSortBy ?? "dueDate";
  const assignedToOthersSortOrder = (filters as any).assignedToOthersSortOrder ?? "asc";
  const setAssignedToOthersFilter = (value: any) => updateFilters({ assignedToOthersFilter: value } as any);
  const setAssignedToOthersSortBy = (value: any) => updateFilters({ assignedToOthersSortBy: value } as any);
  const setAssignedToOthersSortOrder = (value: any) => updateFilters({ assignedToOthersSortOrder: value } as any);

  return (
      <div className="flex-1 px-4 sm:px-6 lg:px-8 py-6">
        {/* Main Tabs */}
        <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 h-auto mb-6">
            <TabsTrigger value="my-tasks" className="flex items-center gap-2" data-testid="tab-my-tasks">
              <ListTodo className="h-4 w-4" />
              My Tasks
            </TabsTrigger>
            <TabsTrigger value="assigned-to-me" className="flex items-center gap-2" data-testid="tab-assigned-to-me">
              <Target className="h-4 w-4" />
              Assigned to Me
            </TabsTrigger>
            <TabsTrigger value="assigned-to-others" className="flex items-center gap-2" data-testid="tab-assigned-to-others">
              <UserPlus className="h-4 w-4" />
              Assigned To Others
            </TabsTrigger>
            <TabsTrigger value="shared" className="flex items-center gap-2" data-testid="tab-shared-tasks">
              <Users className="h-4 w-4" />
              Shared with Me
            </TabsTrigger>
          </TabsList>

          <TabsContent value="my-tasks" className="mt-0">
        {tasksError && (
          <div role="alert" className="mb-4 rounded-md border border-destructive p-4 text-sm">
            Tasks could not be refreshed. <Button variant="outline" size="sm" onClick={() => void refetchTasks()}>Try again</Button>
          </div>
        )}
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-primary" data-testid="stat-total">{taskStats.total}</div>
              <div className="text-sm text-muted-foreground">Total Tasks</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-600" data-testid="stat-active">{taskStats.active}</div>
              <div className="text-sm text-muted-foreground">Active</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600" data-testid="stat-completed">{taskStats.completed}</div>
              <div className="text-sm text-muted-foreground">Completed</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-red-600" data-testid="stat-overdue">{taskStats.overdue}</div>
              <div className="text-sm text-muted-foreground">Overdue</div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filters */}
        <TaskFilterBar
          filters={filters}
          updateFilters={updateFilters}
          isFiltersExpanded={isFiltersExpanded}
          setIsFiltersExpanded={setIsFiltersExpanded}
          setIsQuickProgressOpen={setIsQuickProgressOpen}
          setIsProgressLogOpen={setIsProgressLogOpen}
          categories={categories}
          teamMembers={teamMembers}
          selectedTasks={selectedTasks}
          setSelectedTasks={setSelectedTasks}
          isBulkActionsOpen={isBulkActionsOpen}
          setIsBulkActionsOpen={setIsBulkActionsOpen}
          setPerTaskCompletedOverrides={setPerTaskCompletedOverrides}
          taskStats={taskStats}
        />        {filters.viewMode === "list" && (
          <>
            {/* Tasks Tabs */}
            <Tabs value={filters.filterTab || "all"} onValueChange={(v) => updateFilters({ filterTab: v })} className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="all" className="flex items-center gap-2" data-testid="tab-all">
              <Filter className="h-4 w-4" />
              All
              <Badge variant="secondary" className="ml-1">{taskStats.total}</Badge>
            </TabsTrigger>
            <TabsTrigger value="standalone" className="flex items-center gap-2" data-testid="tab-standalone">
              <Square className="h-4 w-4" />
              Standalone
              <Badge variant="secondary" className="ml-1">{taskStats.standalone}</Badge>
            </TabsTrigger>
            <TabsTrigger value="goals" className="flex items-center gap-2" data-testid="tab-goals">
              <Target className="h-4 w-4" />
              Goals
              <Badge variant="secondary" className="ml-1">{taskStats.linkedToGoals}</Badge>
            </TabsTrigger>
            <TabsTrigger value="milestones" className="flex items-center gap-2" data-testid="tab-milestones">
              <Milestone className="h-4 w-4" />
              Milestones
              <Badge variant="secondary" className="ml-1">{taskStats.linkedToMilestones}</Badge>
            </TabsTrigger>
            <TabsTrigger value="journal" className="flex items-center gap-2" data-testid="tab-journal">
              <BookOpen className="h-4 w-4" />
              Journal
              <Badge variant="secondary" className="ml-1">{taskStats.linkedToJournal}</Badge>
            </TabsTrigger>
          </TabsList>

          {["all", "standalone", "goals", "milestones", "journal"].map(tab => (
            <TabsContent key={tab} value={tab} className="mt-6">
              {filteredAndSortedTasks.length > 0 && (
                <div className="flex items-center gap-3 mb-4 pb-3 border-b">
                  <Checkbox
                    checked={areAllTasksSelected}
                    onCheckedChange={toggleSelectAll}
                    className="h-5 w-5"
                    data-testid="checkbox-select-all-tasks"
                  />
                  <span className="text-sm text-muted-foreground">
                    {areAllTasksSelected ? "Unselect all" : "Select all"} ({filteredAndSortedTasks.length})
                  </span>
                </div>
              )}
              {filteredAndSortedTasks.length === 0 ? (
                (filters.showTopThreeOnly && (starredSubTaskItems?.length ?? 0) > 0) ? null :
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <ListTodo className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium text-foreground mb-2">No tasks found</h3>
                  <p className="text-muted-foreground mb-4">
                    {filters.searchQuery ? "Try a different search term" : "Create your first task to get started!"}
                  </p>
                  <Button onClick={() => setIsCreateModalOpen(true)} data-testid="button-create-first-task">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Task
                  </Button>
                </div>
              ) : groupedFilteredTasks ? (
                <GroupedTasksList
                  viewerId={user?.id}
                  isAssistantViewer={isAssistantViewer}
                  groups={groupedFilteredTasks}
                  collapsedGroups={collapsedGroups}
                  toggleGroupCollapsed={toggleGroupCollapsed}
                  handleDragEnd={handleDragEnd}
                  sensors={sensors}
                  isOverdue={isOverdue}
                  getLinkedItemName={getLinkedItemName}
                  getSubTaskProgress={getSubTaskProgress}
                  toggleTaskMutation={toggleTaskMutation}
                  toggleTopThreeMutation={toggleTopThreeMutation}
                  setViewingTask={setEditingTask}
                  setCreateNoteForTask={setCreateNoteForTask}
                  setEditingTask={setEditingTask}
                  setDeletingTaskId={setDeletingTaskId}
                  selectedTasks={selectedTasks}
                  toggleTaskSelection={toggleTaskSelection}
                  onShareTask={onShareTask}
                  quickProgressPopoverId={quickProgressPopoverId}
                  setQuickProgressPopoverId={setQuickProgressPopoverId}
                  quickProgressPopoverNote={quickProgressPopoverNote}
                  setQuickProgressPopoverNote={setQuickProgressPopoverNote}
                  quickProgressMutation={quickProgressMutation}
                  showSubtasks={filters.showSubtasks}
                  subTasksByTaskId={subTasksByTaskId}
                  onSubtaskToggle={onSubtaskToggle}
                  onSubtaskStar={onSubtaskStar}
                  onSubtaskDateChange={onSubtaskDateChange}
                  onSubtaskTextChange={onSubtaskTextChange}
                  onSubtaskReorder={onSubtaskReorder}
                  onSubtaskDelete={onSubtaskDelete}
                  getHideCompleted={getHideCompleted}
                  onToggleHideCompleted={onToggleHideCompleted}
                  onScheduleTask={onScheduleTask}
                />
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(event) => handleDragEnd(event, filteredAndSortedTasks)}
                >
                  <SortableContext
                    items={filteredAndSortedTasks.map((t: any) => t.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3">
                      {filteredAndSortedTasks.map((task: any) => (
                        <SortableTaskItem
                          key={task.id}
                          task={task}
                          highlightAssigned={isAssignedToAssistant(task, user?.id, isAssistantViewer)}
                          isOverdue={isOverdue}
                          getLinkedItemName={getLinkedItemName}
                          getSubTaskProgress={getSubTaskProgress}
                          toggleTaskMutation={toggleTaskMutation}
                          toggleTopThreeMutation={toggleTopThreeMutation}
                          setViewingTask={setViewingTask}
                          setCreateNoteForTask={setCreateNoteForTask}
                          setEditingTask={setEditingTask}
                          setDeletingTaskId={setDeletingTaskId}
                          selectedTasks={selectedTasks}
                          toggleTaskSelection={toggleTaskSelection}
                          onShareTask={onShareTask}
                          quickProgressPopoverId={quickProgressPopoverId}
                          setQuickProgressPopoverId={setQuickProgressPopoverId}
                          quickProgressPopoverNote={quickProgressPopoverNote}
                          setQuickProgressPopoverNote={setQuickProgressPopoverNote}
                          quickProgressMutation={quickProgressMutation}
                          showSubtasks={filters.showSubtasks}
                          taskSubtasks={subTasksByTaskId.get(task.id) || []}
                          onSubtaskToggle={onSubtaskToggle}
                          onSubtaskStar={onSubtaskStar}
                          onSubtaskDateChange={onSubtaskDateChange}
                          onSubtaskTextChange={onSubtaskTextChange}
                          onSubtaskReorder={onSubtaskReorder}
                          onSubtaskDelete={onSubtaskDelete}
                          hideCompleted={getHideCompleted(task.id)}
                          onToggleHideCompleted={onToggleHideCompleted}
                          onScheduleTask={onScheduleTask}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
              {filters.showTopThreeOnly && (starredSubTaskItems?.length ?? 0) > 0 && (
                <div className="mt-3">
                  <StarredSubTasksSection
                    items={starredSubTaskItems}
                    onToggle={onSubtaskToggle}
                    onStar={onSubtaskStar}
                    onOpenParent={setViewingTask}
                    onDateChange={onSubtaskDateChange}
                  />
                </div>
              )}
            </TabsContent>
          ))}
            </Tabs>
          </>
        )}

        {filters.viewMode === "kanban" && (
          <>
            <div className="h-[calc(100vh-450px)] min-h-[500px]">
            {columnsLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-muted-foreground">Loading columns...</p>
                </div>
              </div>
            ) : (
              <KanbanBoard
                viewerId={user?.id}
                isAssistantViewer={isAssistantViewer}
                tasks={filteredAndSortedTasks}
                columns={columns}
                onTaskMove={handleTaskMove}
                onColumnEdit={(column) => {
                  setEditingColumn(column);
                  setIsColumnDialogOpen(true);
                }}
                onColumnDelete={handleColumnDelete}
                onColumnReorder={handleColumnReorder}
                onCreateTask={(columnId) => {
                  setCreateTaskColumnId(columnId);
                  setIsCreateModalOpen(true);
                }}
                onTaskClick={(task) => setEditingTask(task)}
                onTaskToggle={(taskId, completed) => toggleTaskMutation.mutate({ id: taskId, completed })}
                onTopThreeToggle={(taskId) => toggleTopThreeMutation.mutate(taskId)}
                onTaskEdit={(task) => setEditingTask(task)}
              />
            )}
            </div>
          </>
        )}
          </TabsContent>

          <TabsContent value="assigned-to-me" className="mt-0">
            {assignedToMeError ? (
              <div role="alert" className="rounded-md border border-destructive p-4 text-sm">
                Assigned tasks could not be loaded. <Button variant="outline" size="sm" onClick={() => void refetchAssignedToMe()}>Try again</Button>
              </div>
            ) : assignedToMeLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : assignedToMeTasks.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Tasks Assigned to You</h3>
                  <p className="text-muted-foreground">
                    When friends assign tasks to you, they will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl font-bold text-blue-600" data-testid="stat-assigned-to-me-active">
                        {assignedToMeTasks.filter((t: any) => !t.completed).length}
                      </div>
                      <div className="text-sm text-muted-foreground">Active</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl font-bold text-green-600" data-testid="stat-assigned-to-me-completed">
                        {assignedToMeTasks.filter((t: any) => t.completed).length}
                      </div>
                      <div className="text-sm text-muted-foreground">Completed</div>
                    </CardContent>
                  </Card>
                </div>

                <div className="space-y-3">
                  {assignedToMeTasks.map((task: any) => {
                    const ownerName = task.owner.firstName && task.owner.lastName
                      ? `${task.owner.firstName} ${task.owner.lastName}`
                      : task.owner.username || task.owner.email || "Unknown";
                    const isOverdue = task.dueDate && !task.completed && isPast(endOfDay(new Date(task.dueDate)));
                    
                    return (
                      <Card
                        key={task.id}
                        className={`cursor-pointer transition-colors ${
                          isAssignedToAssistant(task, user?.id, isAssistantViewer)
                            ? assignedTaskCardClass
                            : "hover:bg-muted/50"
                        } ${task.completed ? 'opacity-60' : ''}`}
                        onClick={() => {
                          const taskToEdit = tasks.find((t: any) => t.id === task.id) || task;
                          setEditingTask(taskToEdit);
                        }}
                        data-testid={`assigned-to-me-task-${task.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={task.completed}
                              onClick={(e) => e.stopPropagation()}
                              onCheckedChange={async (checked) => {
                                try {
                                  await apiRequest("PATCH", `/api/tasks/${task.id}`, {
                                    completed: checked,
                                  });
                                  queryClient.invalidateQueries({ queryKey: ["/api/tasks/assigned-to-me"] });
                                  queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
                                } catch (error) {
                                  console.error("Failed to update task:", error);
                                  toastTaskMutationError("Couldn't update task", error, "Your change was not saved.");
                                }
                              }}
                              data-testid={`checkbox-assigned-task-${task.id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className={`font-medium ${task.completed ? 'line-through text-muted-foreground' : ''}`}>
                                  {task.title}
                                </h4>
                                <Badge variant={task.priority === "high" ? "destructive" : task.priority === "medium" ? "default" : "secondary"} className="text-xs">
                                  {task.priority}
                                </Badge>
                              </div>
                              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Users className="h-3 w-3" />
                                  Assigned by {ownerName}
                                </span>
                                {task.dueDate && (
                                  <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-500 font-medium' : ''}`}>
                                    <Calendar className="h-3 w-3" />
                                    {format(new Date(task.dueDate), "MMM d, yyyy")}
                                    {isOverdue && " (Overdue)"}
                                  </span>
                                )}
                                {sanitizeTaskCategory(task.category) && sanitizeTaskCategory(task.category) !== "Uncategorized" && (
                                  <span className="flex items-center gap-1">
                                    <Tag className="h-3 w-3" />
                                    {sanitizeTaskCategory(task.category)}
                                  </span>
                                )}
                              </div>
                              {task.description && (
                                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{task.description}</p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="assigned-to-others" className="mt-0">
            {(() => {
              // Filter tasks assigned to others
              let assignedToOthersTasks = tasks.filter((t: any) => t.assignedTo === "Others");
              
              // Apply completion filter
              if (assignedToOthersFilter === "active") {
                assignedToOthersTasks = assignedToOthersTasks.filter((t: any) => !t.completed);
              } else if (assignedToOthersFilter === "completed") {
                assignedToOthersTasks = assignedToOthersTasks.filter((t: any) => t.completed);
              }
              
              // Apply sorting
              assignedToOthersTasks = [...assignedToOthersTasks].sort((a: any, b: any) => {
                let comparison = 0;
                if (assignedToOthersSortBy === "dueDate") {
                  const dateA = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
                  const dateB = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
                  comparison = dateA - dateB;
                } else if (assignedToOthersSortBy === "title") {
                  comparison = a.title.localeCompare(b.title);
                } else if (assignedToOthersSortBy === "assignee") {
                  comparison = (a.assignedToOther || "").localeCompare(b.assignedToOther || "");
                }
                return assignedToOthersSortOrder === "asc" ? comparison : -comparison;
              });

              const activeCount = tasks.filter((t: any) => t.assignedTo === "Others" && !t.completed).length;
              const completedCount = tasks.filter((t: any) => t.assignedTo === "Others" && t.completed).length;

              return (
                <div className="space-y-6">
                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4">
                    <Card>
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-blue-600" data-testid="stat-assigned-active">{activeCount}</div>
                        <div className="text-sm text-muted-foreground">Active</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-green-600" data-testid="stat-assigned-completed">{completedCount}</div>
                        <div className="text-sm text-muted-foreground">Completed</div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Filters and Sort */}
                  <div className="flex flex-wrap gap-4 items-center">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Filter:</span>
                      <Select value={assignedToOthersFilter} onValueChange={(v) => setAssignedToOthersFilter(v as "all" | "active" | "completed")}>
                        <SelectTrigger className="w-32" data-testid="select-assigned-filter">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Sort by:</span>
                      <Select value={assignedToOthersSortBy} onValueChange={(v) => setAssignedToOthersSortBy(v as "dueDate" | "title" | "assignee")}>
                        <SelectTrigger className="w-32" data-testid="select-assigned-sort">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="dueDate">Due Date</SelectItem>
                          <SelectItem value="title">Title</SelectItem>
                          <SelectItem value="assignee">Assignee</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setAssignedToOthersSortOrder(assignedToOthersSortOrder === "asc" ? "desc" : "asc")}
                        data-testid="button-assigned-sort-order"
                      >
                        {assignedToOthersSortOrder === "asc" ? "↑" : "↓"}
                      </Button>
                    </div>
                  </div>

                  {/* Task List */}
                  {assignedToOthersTasks.length === 0 ? (
                    <Card className="border-dashed">
                      <CardContent className="p-8 text-center">
                        <UserPlus className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-semibold mb-2">No Tasks Assigned to Others</h3>
                        <p className="text-muted-foreground mb-4">
                          Tasks you assign to other people will appear here
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground" data-testid="text-assigned-tasks-count">
                        {assignedToOthersTasks.length} task{assignedToOthersTasks.length !== 1 ? 's' : ''} assigned to others
                      </p>
                      {assignedToOthersTasks.map((task: any) => (
                        <Card 
                          key={task.id} 
                          className={`cursor-pointer hover:bg-muted/50 transition-colors ${task.completed ? 'opacity-60' : ''}`}
                          onClick={() => setEditingTask(task)}
                          data-testid={`card-assigned-task-${task.id}`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleTaskMutation.mutate({ id: task.id, completed: !task.completed });
                                }}
                                className="mt-1"
                              >
                                {task.completed ? (
                                  <CheckSquare className="h-5 w-5 text-green-600" />
                                ) : (
                                  <Square className="h-5 w-5 text-muted-foreground" />
                                )}
                              </button>
                              <div className="flex-1 min-w-0">
                                <h4 className={`font-medium ${task.completed ? 'line-through text-muted-foreground' : ''}`}>
                                  {task.title}
                                </h4>
                                <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <UserPlus className="h-3 w-3" />
                                    {task.assignedToOther || "Unknown"}
                                  </span>
                                  {task.dueDate && (
                                    <span className={`flex items-center gap-1 ${isOverdue(task) ? 'text-red-600' : ''}`}>
                                      <Calendar className="h-3 w-3" />
                                      {format(new Date(task.dueDate), "MMM d, yyyy")}
                                    </span>
                                  )}
                                  {task.priority && (
                                    <Badge variant={task.priority === "high" ? "destructive" : task.priority === "medium" ? "default" : "secondary"}>
                                      {task.priority}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </TabsContent>

          <TabsContent value="shared" className="mt-0">
            {sharedTasksLoading ? (
              <div className="flex items-center justify-center min-h-[30vh]">
                <div className="text-center">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-muted-foreground">Loading shared tasks...</p>
                </div>
              </div>
            ) : sharedTasks.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Shared Tasks</h3>
                  <p className="text-muted-foreground mb-4">
                    Tasks shared with you by friends will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground" data-testid="text-shared-tasks-count">
                  {sharedTasks.length} task{sharedTasks.length !== 1 ? 's' : ''} shared with you
                </p>
                <div className="space-y-3">
                  {sharedTasks.map((sharedTask: any) => {
                    // The API returns flat TaskWithOwner rows (task columns spread
                    // at the top level plus `permission` and `owner`). SharedTaskCard
                    // expects a nested { task: {...}, taskId, permission, owner }
                    // shape, so normalize here before passing through.
                    const normalized = sharedTask.task
                      ? sharedTask
                      : {
                          ...sharedTask,
                          taskId: sharedTask.id,
                          task: {
                            id: sharedTask.id,
                            title: sharedTask.title,
                            description: sharedTask.description ?? null,
                            completed: sharedTask.completed,
                            dueDate: sharedTask.dueDate ?? null,
                            priority: sharedTask.priority,
                          },
                        };
                    return (
                      <SharedTaskCard
                        key={normalized.taskId ?? normalized.id}
                        sharedTask={normalized}
                        updateSharedTaskMutation={updateSharedTaskMutation}
                        toast={toast}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
  );
}
